import { toast } from 'sonner';

export interface HandleErrorOptions {
  /** Short dot.path identifier for logs, e.g. 'inventory.bulkDelete'. */
  context: string;
  /** Overrides the default toast title. */
  userMessage?: string;
  /** Suppresses the toast (still logs in dev). */
  silent?: boolean;
  /** Visual tone of the toast. */
  tone?: 'destructive' | 'warning' | 'info';
}

const isDev = typeof import.meta !== 'undefined' && !!import.meta.env?.DEV;

function extractMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const e = err as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message) return e.message;
    if (typeof e.error_description === 'string' && e.error_description) return e.error_description as string;
    if (typeof e.hint === 'string' && e.hint) return e.hint as string;
  }
  return undefined;
}

/**
 * Single entry point for handling caught errors. Logs in dev + fires a toast
 * (unless silent). Extracts human-readable text from Supabase-shaped errors.
 */
export function handleError(err: unknown, opts: HandleErrorOptions): void {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.error(`[${opts.context}]`, err);
  }

  if (opts.silent) return;

  const title = opts.userMessage ?? extractMessage(err) ?? 'Something went wrong';

  switch (opts.tone) {
    case 'destructive':
      toast.error(title);
      return;
    case 'warning':
      toast.warning(title);
      return;
    case 'info':
      toast.info(title);
      return;
    default:
      toast(title);
  }
}
